import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getExpos, deleteExpo, sendMoveOtp, verifyMove } from "../Apis/ExpoAPIs";
import { Tooltip } from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { toast } from "react-toastify";
import { formatDate } from "../helper/DateHelper";
import usePermissions from "../hook/userPermission";

const ExpoListLayer = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [expos, setExpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expoToDelete, setExpoToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Move to Present Workflow States
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [expoToMove, setExpoToMove] = useState(null);
  const [managerEmail, setManagerEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");

  const fetchExpos = async () => {
    setLoading(true);
    const res = await getExpos({
      page: currentPage,
      limit: rowsPerPage,
      search: searchTerm
    });
    if (res.status) {
      setExpos(res.response?.data || res.response || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExpos();

    // Get manager email from localStorage
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setManagerEmail(user.email || "");
      } catch (e) {
        console.error("Error parsing userData", e);
      }
    }
  }, [currentPage, rowsPerPage, searchTerm]);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map((tooltipTriggerEl) => {
      return new Tooltip(tooltipTriggerEl);
    });
    return () => {
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, [expos]);

  const totalRecords = expos.length;
  const currentExpos = expos;
  const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleDeleteClick = (expo) => {
    setExpoToDelete(expo);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (expoToDelete) {
      setIsUpdating(true);
      const res = await deleteExpo(expoToDelete._id || expoToDelete.id);
      if (res.status) {
        fetchExpos();
        setShowDeleteModal(false);
        setExpoToDelete(null);
      }
      setIsUpdating(false);
    }
  };

  const handleMoveClick = (expo) => {
    setExpoToMove(expo);
    setShowMoveModal(true);
    setOtp("");
    setOtpError("");
    setIsOtpSent(false);
  };

  const handleSendOtp = async () => {
    if (!managerEmail) {
      toast.error("Manager email not found!");
      return;
    }

    setIsSendingOtp(true);
    const res = await sendMoveOtp({ expoId: expoToMove._id || expoToMove.id });
    if (res.status) {
      setIsOtpSent(true);
      toast.success(`OTP sent to ${managerEmail}`);
    }
    setIsSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }
    setIsVerifying(true);
    setOtpError("");
    const res = await verifyMove({
      expoId: expoToMove._id || expoToMove.id,
      otp
    });

    if (res.status) {
      toast.success("Verification successful! Expo moved to present.");
      setShowMoveModal(false);
      setExpoToMove(null);
      fetchExpos();
    } else {
      const errorMsg = res.response?.message || "Invalid OTP. Please try again.";
      setOtpError(errorMsg);
    }
    setIsVerifying(false);
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div
          className="card-header border-bottom py-12 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between"
          style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}
        >
          <h6 className="text-white mb-0">Upcoming Expos</h6>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40 py-10 bg-white border-0"
                style={{ height: "44px" }}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Icon icon="ion:search-outline" className="icon" style={{ color: "black" }} />
            </div>
            {hasPermission("upcoming_expo", "add") && (
              <Link
                to="/upcoming-expo/add"
                className="btn text-sm btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl" />
                Add Expo
              </Link>
            )}
          </div>
        </div>
        <div className="card-body p-24">
          <div className="table-responsive">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ backgroundColor: "#f7e7da" }}>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Expo Name</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Manager</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Location</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Date</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Type</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Status</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="text-center py-24">Loading...</td></tr>
                ) : currentExpos.length > 0 ? (
                  currentExpos.map((expo, index) => (
                    <tr key={expo._id || expo.id}>
                      <td>{currentPage * rowsPerPage + index + 1}</td>
                      <td>{expo.expoName}</td>
                      <td>
                        {Array.isArray(expo.eventManager)
                          ? expo.eventManager.map((m) => m.name).filter(Boolean).join(", ")
                          : (expo.eventManager?.name || "-")}
                      </td>
                      <td>
                        {(() => {
                          const address = typeof expo.location === 'object' ? (expo.location?.address || JSON.stringify(expo.location)) : (expo.location || "-");
                          const displayAddress = address.length > 30 ? address.substring(0, 30) + "..." : address;
                          return (
                            <span
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={address}
                              style={{ cursor: 'help' }}
                            >
                              {displayAddress}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{formatDate(expo.startDate)} - {formatDate(expo.endDate)}</td>
                      <td>
                        <span
                          className="badge px-12 py-6 radius-8 fw-semibold"
                          style={{
                            backgroundColor:
                              (expo.entryType || "").toLowerCase() === "paid"
                                ? "#d1fae5"
                                : (expo.entryType || "").toLowerCase() === "free"
                                  ? "#e0f2fe"
                                  : "#f3f4f6",
                            color:
                              (expo.entryType || "").toLowerCase() === "paid"
                                ? "#065f46"
                                : (expo.entryType || "").toLowerCase() === "free"
                                  ? "#0369a1"
                                  : "#374151",
                            fontSize: "12px",
                          }}
                        >
                          {expo.entryType || "-"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="badge px-12 py-6 radius-8 fw-semibold"
                          style={{
                            backgroundColor: expo.isActive !== false ? "#d1fae5" : "#fee2e2",
                            color: expo.isActive !== false ? "#065f46" : "#991b1b",
                            fontSize: "12px",
                          }}
                        >
                          {expo.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          {hasPermission("upcoming_expo", "view") && (
                            <Link
                              to={`/upcoming-expo/view/${expo._id || expo.id}`}
                              className="btn-view-custom fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                              style={{ backgroundColor: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff" }}
                              title="View Expo"
                            >
                              <Icon icon="lucide:eye" />
                            </Link>
                          )}
                          {hasPermission("upcoming_expo", "view") && (
                            <Link
                              to={`/upcoming-expo/exhibition-layout/${expo._id || expo.id}`}
                              className="btn-edit-custom1 fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Exhibition Layout"
                            >
                              <Icon icon="solar:box-bold" className="icon text-xl" />
                            </Link>
                          )}
                          {hasPermission("upcoming_expo", "edit") && (
                            <Link
                              to={`/upcoming-expo/edit/${expo._id || expo.id}`}
                              className="btn-edit-custom fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Edit Expo"
                            >
                              <Icon icon="lucide:edit" />
                            </Link>
                          )}
                          {hasPermission("upcoming_expo", "edit") && (
                            <button
                              onClick={() => handleMoveClick(expo)}
                              className="bg-success-focus text-success-600 fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              title="Move to Present"
                            >
                              <Icon icon="lucide:arrow-right-circle" className="text-xl" />
                            </button>
                          )}
                          {hasPermission("upcoming_expo", "delete") && (
                            <button
                              onClick={() => handleDeleteClick(expo)}
                              className="bg-danger-focus text-danger-600 fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              title="Delete Expo"
                            >
                              <Icon icon="lucide:trash-2" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6" className="text-center py-24">No expos found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalRecords}
          />
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:trash-bin-trash-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-2">Are you sure?</h5>
          <p className="text-secondary-light mb-4">Do you want to delete expo "{expoToDelete?.expoName}"?</p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-secondary" className="px-32 radius-8" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button
              className="px-32 radius-8 border-0"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Move to Present Modal */}
      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)} centered size="md">
        <Modal.Header className="border-bottom py-16 px-24" style={{ background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)" }}>
          <Modal.Title className="text-white h6 mb-0 fw-bold">Move Expo to Present</Modal.Title>
          <button type="button" className="btn-close btn-close-white" onClick={() => setShowMoveModal(false)}></button>
        </Modal.Header>
        <Modal.Body className="p-32">
          <div className="text-center mb-24">
            <div className="bg-success-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-16">
              <Icon icon="solar:double-alt-arrow-right-bold" className="text-success-600 text-xxl" />
            </div>
            <h6 className="fw-bold mb-4">Verification Required</h6>
            <p className="text-secondary-light text-sm">You are moving <strong>{expoToMove?.expoName}</strong> to the present expo list. Please verify your identity.</p>
          </div>

          <div className="mb-24">
            <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-8">Manager Email</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0 radius-start-8">
                <Icon icon="solar:letter-bold" className="text-secondary-light" />
              </span>
              <input
                type="email"
                className="form-control radius-end-8 ps-0"
                value={managerEmail}
                readOnly
                placeholder="manager@example.com"
              />
            </div>
          </div>
          {!isOtpSent ? (
            <div className="d-grid mt-32">
              <Button
                className="py-12 fw-bold radius-8 border-0 shadow-sm d-flex align-items-center justify-content-center gap-2"
                onClick={handleSendOtp}
                disabled={isSendingOtp}
                style={{ backgroundColor: "#ed1c24" }}
              >
                {isSendingOtp && <Icon icon="line-md:loading-twotone-loop" className="text-xl" />}
                {isSendingOtp ? "Sending OTP..." : "Send OTP to Email"}
              </Button>
            </div>
          ) : (
            <div className="mt-24 animation-fade-in">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-8">Enter 6-Digit OTP</label>
              <div className="d-flex flex-column gap-2 mb-24">
                <input
                  type="text"
                  className={`form-control text-center fw-bold text-xl radius-8 h-56-px ${otpError ? 'border-danger' : ''}`}
                  placeholder="0 0 0 0 0 0"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, ""));
                    setOtpError("");
                  }}
                />
                {otpError && <span className="text-danger text-sm fw-medium text-center animation-fade-in">{otpError}</span>}
              </div>
              <div className="d-grid gap-3">
                <Button
                  className="py-12 fw-bold radius-8 border-0 shadow-sm"
                  onClick={handleVerifyOtp}
                  disabled={isVerifying}
                  style={{ backgroundColor: "#28a745", color: "white" }}
                >
                  {isVerifying ? "Verifying..." : "Verify & Move Expo"}
                </Button>
                <button
                  className="btn btn-link text-secondary-light text-sm fw-medium p-0"
                  onClick={() => setIsOtpSent(false)}
                >
                  Didn't receive code? Resend
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ExpoListLayer;
